import { Editor } from './editor.mjs';
import { Legend } from './legend.mjs';
import { Library } from './library.mjs';
import { Radio } from './radio.mjs';
import { Scope } from './scope.mjs';
import { TrackList } from './track-list.mjs';
import { UI } from './ui.mjs';
import { VibingCat } from './vibing-cat.mjs';
import { Visualizer } from './visualizer.mjs';
import { getCodeFromUrl, getUrlFromCode } from './url.mjs';
import { Highlighter } from './highlighter.mjs';
import { LOOP_SAMPLES } from './loop-samples.mjs';
import { CodeCanvas } from './code-canvas.mjs';
import { MidiInput } from './midi.mjs';
import Piano from 'clayton-piano';

const editor = new Editor();
const legend = new Legend();
const library = new Library();
const radio = new Radio();
const scope = new Scope();
const ui = new UI();
const visualizer = new Visualizer();
const vibingCat = new VibingCat();
const highlighter = new Highlighter();
const midi = new MidiInput();
let codeCanvas = null;
// A real, decodable silent WAV used purely to anchor the OS media session
// (Android notification card, lock-screen widget, Bluetooth AVRCP, macOS
// media keys). See setupMediaSession() for why a backing HTMLMediaElement
// is unavoidable for procedurally-generated WebAudio.
//
// Built at runtime as a Blob URL rather than a checked-in file or a giant
// base64 literal. A blob: URL decodes through the browser's normal media
// pipeline, so Chrome grants it a media-session notification (an
// srcObject=MediaStream element does NOT — that uses the WebRTC path,
// which is why an earlier attempt killed the card entirely). It also ships
// inside the JS bundle with zero deploy/path risk: nothing separate to 404
// on Cloudflare Pages or in the iOS app bundle. 6 s clears Chrome's >5 s
// "full audio focus" threshold; 8 kHz / 8-bit / mono keeps it ~48 KB.
// 128 == digital zero for unsigned 8-bit PCM.
function makeSilenceWavUrl(seconds = 6, sampleRate = 8000) {
	const dataSize = seconds * sampleRate; // 8-bit mono → 1 byte per sample
	const buf = new ArrayBuffer(44 + dataSize);
	const view = new DataView(buf);
	const writeStr = (off, str) => {
		for(let i = 0; i < str.length; i++) {
			view.setUint8(off + i, str.charCodeAt(i));
		}
	};
	writeStr(0, 'RIFF');
	view.setUint32(4, 36 + dataSize, true);
	writeStr(8, 'WAVE');
	writeStr(12, 'fmt ');
	view.setUint32(16, 16, true);         // fmt chunk size
	view.setUint16(20, 1, true);          // PCM
	view.setUint16(22, 1, true);          // mono
	view.setUint32(24, sampleRate, true); // sample rate
	view.setUint32(28, sampleRate, true); // byte rate (1 byte/sample)
	view.setUint16(32, 1, true);          // block align
	view.setUint16(34, 8, true);          // bits per sample
	writeStr(36, 'data');
	view.setUint32(40, dataSize, true);
	new Uint8Array(buf, 44).fill(128);    // silence
	return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
}
/// Load a track via the bytebeat composer, fetching its code from
/// data/songs/<type>/<hash>.js if it's a file-based library entry
/// (no inline code in the upstream library HTML). Without this fetch
/// step, Next/Prev would silently load empty code for file-based
/// entries — which is exactly the bug a user hit after the 3rd track,
/// where the 4th turned out to be file-based.
async function radioLoadAndPlay(track, autoPlay = true) {
	if(!track) return;
	let code = track.code;
	if(!code && track.codeFile) {
		try {
			const type = track.codeType || 'minified';
			const url = `./data/songs/${ type }/${ track.codeFile }`;
			const res = await fetch(url);
			code = await res.text();
		} catch(e) {
			console.error('[radio] failed to fetch track file:', e);
			return;
		}
	}
	if(!code) return;
	globalThis.bytebeat.loadCode({
		code,
		sampleRate: track.sampleRate || 8000,
		mode: track.mode || 'Bytebeat',
	}, autoPlay);
}

const trackList = new TrackList(radio, track => {
	// User clicked a row in the Favorites list → sync radio cursor + play.
	radio.setCurrent(track);
	radioLoadAndPlay(track);
});
// The radio's "universe" of tracks is whatever the composer's library
// has surfaced so far (TrackList watches every library container with a
// MutationObserver and records each `.entry` as the user expands it).
radio.setUniverseProvider(() => trackList.getKnownTracks());

document.title = 'Bytebeat radio · b897b2a';
globalThis.bytebeat = new class {
	constructor() {
		this.audioCtx = null;
		this.audioGain = null;
		this.audioRecordChunks = [];
		this.audioRecorder = null;
		this.audioWorkletNode = null;
		this.byteSample = 0;
		this.defaultSettings = {
			codeStyle: 'Atom Dark',
			colorDiagram: '#0080ff',
			colorStereo: 1,
			colorTimeCursor: '#80bbff',
			colorWaveform: '#ffffff',
			drawMode: scope.drawMode,
			drawScale: scope.drawScale,
			fftSize: scope.fftSize,
			isSeconds: false,
			showAllSongs: library.showAllSongs,
			srDivisor: 1,
			themeStyle: 'Default Dark',
			volume: .5
		};
		this.isCompilationError = false;
		this.isNeedClear = false;
		this.isLagging = false;
		this.isPlaying = false;
		this.isRecording = false;
		this.lastUpdateTime = 0;
		this.mode = 'Bytebeat';
		this.playbackSpeed = 1;
		this.sampleRate = 8000;
		this.settings = this.defaultSettings;
		this.updateCounter = 0;
		this.init();
	}
	handleEvent(event) {
		let elem = event.target;
		const { classList } = elem;
		switch(event.type) {
		case 'change':
			switch(elem.id) {
			case 'control-code-style': this.setCodeStyle(elem.value); break;
			case 'control-color-diagram': this.setColorDiagram(elem.value); break;
			case 'control-color-stereo':
				this.setColorStereo(+elem.value);
				ui.controlColorDiagramInfo.innerHTML = scope.getColorTest('colorDiagram');
				ui.controlColorWaveformInfo.innerHTML = scope.getColorTest('colorWaveform');
				break;
			case 'control-color-timecursor': this.setColorTimeCursor(elem.value); break;
			case 'control-color-waveform': this.setColorWaveform(elem.value); break;
			case 'control-drawmode': this.setDrawMode(elem.value); break;
			case 'control-mode': this.setPlaybackMode(elem.value); break;
			case 'control-samplerate':
			case 'control-samplerate-select': this.setSampleRate(+elem.value); break;
			case 'control-theme-style': this.setThemeStyle(elem.value); break;
			case 'library-show-all':
				library.toggleAll(elem, elem.checked);
				this.saveSettings();
				break;
			}
			return;
		case 'click':
			switch(elem.tagName) {
			case 'svg': elem = elem.parentNode; break;
			case 'use': elem = elem.parentNode.parentNode; break;
			default:
				if(classList.contains('control-fast-multiplier')) {
					elem = elem.parentNode;
				}
			}
			switch(elem.id) {
			case 'canvas-container':
			case 'canvas-main':
			case 'canvas-play':
			case 'canvas-timecursor': this.playbackToggle(!this.isPlaying); break;
			case 'control-counter':
			case 'control-pause': this.playbackToggle(false); break;
			case 'control-expand': ui.expandEditor(); break;
			case 'control-legend':
			case 'legend-handle':
				legend.toggle();
				break;
			case 'control-link': ui.copyLink(); break;
			case 'control-play-backward': this.playbackToggle(true, true, -1); break;
			case 'control-play-forward': this.playbackToggle(true, true, 1); break;
			case 'control-next-track': this.radioAdvance(1); break;
			case 'control-prev-track': this.radioAdvance(-1); break;
			case 'control-shuffle':
				radio.toggleMode('shuffle');
				this._syncRadioToolbar();
				break;
			case 'control-lock-fav':
				radio.toggleMode('lockFavorites');
				this._syncRadioToolbar();
				break;
			case 'control-viz': visualizer.toggle(); break;
			case 'control-viz-next': visualizer.nextPreset(); break;
			case 'control-viz-auto': visualizer.toggleAutoChange(); break;
			case 'control-cat': vibingCat.toggle(); break;
			case 'control-piano': if (this._pianoInstance) this._hidePiano(); else this._showPiano(); break;
			case 'control-help': this._toggleHelpModal(true); break;
				case 'control-settings': this._toggleSettingsModal(true); break;
			case 'control-compact': {
				const on = !document.body.classList.contains('compact-mode');
				document.body.classList.toggle('compact-mode', on);
				try { localStorage.setItem('coderadio.compact', on ? '1' : '0'); } catch(_) {}
				break;
			}
			case 'control-rate-up':
				if(radio.currentTrack) radio.setRating(radio.currentTrack, 'up');
				break;
			case 'control-rate-down':
				if(radio.currentTrack) radio.setRating(radio.currentTrack, 'down');
				break;
			case 'control-rate-fav':
				if(radio.currentTrack) radio.toggleFavorite(radio.currentTrack);
				break;
			case 'control-rec': this.toggleRecording(); break;
			case 'control-reset': this.resetTime(); break;
			case 'control-scale': this.resetScopeAdjustment(); break;
			case 'control-scaledown': this.setScopeAdjustment(-1, elem); break;
			case 'control-scaleup': this.setScopeAdjustment(1); break;
			case 'control-srdivisor-down': this.setSRDivisor(-1); break;
			case 'control-srdivisor-up': this.setSRDivisor(1); break;
			case 'control-stop': this.playbackStop(); break;
			case 'control-counter-units': this.toggleCounterUnits(); break;
			default:
				switch(true) {
				case classList.contains('code-text'):
					this._radioMarkFromEntry(elem);
					this.loadCode(Object.assign({ code: elem.innerText },
						elem.hasAttribute('data-songdata') ? JSON.parse(elem.dataset.songdata) : {}));
					break;
				case classList.contains('code-load'):
					this._radioMarkFromEntry(elem);
					library.onclickCodeLoadButton(elem);
					break;
				case classList.contains('code-remix-load'): library.onclickRemixLoadButton(elem); break;
				case classList.contains('library-header'): library.onclickLibraryHeader(elem); break;
				case elem.parentNode.classList.contains('library-header'):
					library.onclickLibraryHeader(elem.parentNode);
					break;
				case classList.contains('song-hash'):
					navigator.clipboard.writeText(elem.dataset.hash);
					event.preventDefault();
					break;
				}
			}
			return;
		case 'input':
			switch(elem.id) {
			case 'control-counter': this.oninputCounter(event); break;
			case 'control-volume': this.setVolume(false); break;
				case 'control-zoom': {
					const size = elem.value + 'pt';
					document.documentElement.style.setProperty('--code-font-size', size);
					// CodeMirror caches line heights — tell it to re-measure.
					if (editor.view) editor.view.requestMeasure();
					try { localStorage.setItem('coderadio.codeFontSize', size); } catch(_) {}
					break;
				}
			}
			return;
		case 'keydown':
			if(elem.id === 'control-counter') {
				this.oninputCounter(event);
			}
			return;
		case 'mouseover':
			switch(true) {
			case classList.contains('code-load'):
				elem.title = `Click to play the ${ elem.dataset.type } code`;
				break;
			case classList.contains('code-text'): elem.title = 'Click to play this code'; break;
			case classList.contains('songs-header'): elem.title = 'Click to show/hide the songs'; break;
			case classList.contains('song-hash'):
				elem.title = 'Click to copy the song hash into clipboard';
				break;
			case classList.contains('tag-c'): elem.title = 'C-compatible code'; break;
			case classList.contains('tag-console'):
				elem.title = 'Outputs messages in the error console';
				break;
			case classList.contains('tag-drawing'):
				elem.title = 'Generates art in the visualiser\'s scope';
				break;
			case classList.contains('tag-sample'):
				elem.title = 'Uses encoded audio samples (PCM, for example)';
				break;
			case classList.contains('tag-slow'):
				elem.title = 'May be performance issues. Try switching Chrome/Firefox.';
				break;
			}
			return;
		}
	}
	async init() {
		try {
			this.settings = JSON.parse(localStorage.settings);
			scope.drawMode = this.settings.drawMode;
			scope.drawScale = this.settings.drawScale;
			scope.setFFTSize(+this.settings.fftSize || 10);
			library.showAllSongs = this.settings.showAllSongs;
		} catch(err) {
			this.saveSettings();
		}
		this.setThemeStyle();
		await this.initAudio();
		if(document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.initAfterDom());
			return;
		}
		this.initAfterDom();
	}
	initAfterDom() {
		editor.init();
		codeCanvas = new CodeCanvas(editor.view);
		// Expose editor + legend on the global so devtools can poke around
		// and so callers (e.g. legend "Try" buttons) reach the editor.
		this.editor = editor;
		this.legend = legend;
		legend.initElements();
		// Radio core — the universe is the composer's existing library
		// entries (captured by TrackList's MutationObserver as the user
		// expands each library section). Favorites + ratings persist to
		// localStorage with their metadata, so the Favorites panel works
		// regardless of which libraries the user has loaded.
		this.radio = radio;
		this.trackList = trackList;
		this.visualizer = visualizer;
		this.vibingCat = vibingCat;
		trackList.initElements();
		visualizer.initElements();
		vibingCat.initElements();
		this._syncRadioToolbar();
		// Compact mode — minimal UI showing only the radio controls.
		// Default: ON for mobile-sized viewports (≤ 768px), OFF for
		// desktop. Saved preference always wins after first interaction.
		(function applyCompactMode() {
			const saved = localStorage.getItem('coderadio.compact');
			let on;
			if(saved !== null) {
				on = saved === '1';
			} else {
				on = window.matchMedia('(max-width: 768px)').matches;
			}
			document.body.classList.toggle('compact-mode', on);
		})();
		// Restore saved code font size + sync the zoom slider.
		(function applyFontSize() {
			const saved = localStorage.getItem('coderadio.codeFontSize');
			if (saved) document.documentElement.style.setProperty('--code-font-size', saved);
			const zoom = document.getElementById('control-zoom');
			if (zoom && saved) zoom.value = parseFloat(saved);
		})();
		// Live code highlighting — per-mode toggles, all default on.
		(function loadHighlightSettings() {
			try {
				const saved = localStorage.getItem('coderadio.hlTernary');
				highlighter.ternary = saved === null ? true : saved === '1';
			} catch { highlighter.ternary = true; }
			try {
				const saved = localStorage.getItem('coderadio.hlShortCircuit');
				highlighter.shortCircuit = saved === null ? true : saved === '1';
			} catch { highlighter.shortCircuit = true; }
			try {
				const saved = localStorage.getItem('coderadio.hlBitShift');
				highlighter.bitShift = saved === null ? true : saved === '1';
			try {
				const saved = localStorage.getItem('coderadio.hlOpGlow');
				highlighter.operatorGlow = saved === null ? true : saved === '1';
			} catch { highlighter.operatorGlow = true; }
			} catch { highlighter.bitShift = true; }
		})();
		// MIDI input — start listening + wire transposition callback.
		(function initMidi() {
			midi._onChange = () => { if (this.audioWorkletNode) this._syncMidiRatio(); };
		// Piano → MIDI pipeline
			try {
				midi.enabled = localStorage.getItem('coderadio.midiEnabled') === '1';
				midi.transposeMode = localStorage.getItem('coderadio.midiMode') || 'speed';
				midi.scale = localStorage.getItem('coderadio.midiScale') || 'Chromatic';
			} catch {}
			if (midi.enabled) midi.start();
		}).call(this);
		// Auto-advance (Continuous Play) settings — persisted to localStorage.
		(function loadAdvanceSettings() {
			try { this.continuousPlay = localStorage.getItem('coderadio.continuousPlay') === '1'; }
			catch { this.continuousPlay = false; }
			try { this.minPlaySec = parseInt(localStorage.getItem('coderadio.minPlaySec'), 10) || 30; }
			catch { this.minPlaySec = 30; }
			try { this.maxLoopSec = parseInt(localStorage.getItem('coderadio.maxLoopSec'), 10) || 120; }
			catch { this.maxLoopSec = 120; }
			try { this.evolvingMaxSec = parseInt(localStorage.getItem('coderadio.evolvingMaxSec'), 10) || 3600; }
			catch { this.evolvingMaxSec = 3600; }
			this._advanceTimer = null;
		}).call(this);
		// Auto-load the Classic library on first page open so radio
		// Next/Prev has tracks immediately. Without this, the universe
		// stays empty until the user manually expands a library section,
		// and the radio buttons appear non-functional.
		//
		// This is async (fetch + ungzip + innerHTML), so everything that
		// depends on the universe being populated — restoreLastTrack,
		// _syncNowRating, _updateCurrent — must wait for it to resolve.
		(async () => {
			const classicContainer = document.getElementById('library-classic');
			if(classicContainer) {
				const summary = classicContainer.previousElementSibling;
				if(summary && summary.classList.contains('library-header')) {
					try { await library.onclickLibraryHeader(summary); }
					catch(e) { console.error('auto-load Classic failed:', e); }
				}
			}
			// Now the universe is seeded. Try to resume the last-played
			// track. If this is a fresh session (incognito / first visit),
			// default to "the 42 melody" — the canonical bytebeat one-liner.
			radio.restoreLastTrack();
			if(!radio.currentTrack) {
				const universe = trackList.getKnownTracks();
				const def = universe.find(t => t.hash === "e295af172c127b1f527c823ad0aeaeda");
				if(def) radio.setCurrent(def);
			}
			// Default shuffle ON for new sessions so visitors land in
			// radio mode immediately. The first click of 🔀 toggles it off.
			if(localStorage.getItem("coderadio.modes") === null) {
				radio.setMode("shuffle", true);
			}
			this._syncRadioToolbar();
			this._syncNowRating();
			trackList._updateCurrent();
			// restoreLastTrack() sets currentTrack WITHOUT emitting a
			// 'current' event (the manual syncs above stand in for the other
			// subscribers), so the media-session metadata subscriber never
			// fires on a page-load restore. Prime the Now Playing card here,
			// or it shows the page <title> ("Bytebeat composer") until the
			// first Next.
			this._updateMediaMetadata();
			// Load the restored/default track's code into the editor so the
			// user sees it immediately, but DON'T auto-start playback — the
			// browser's autoplay policy keeps the AudioContext suspended
			// until a user gesture. Auto-playing would toggle the button
			// to "pause" while the AudioContext is still locked, making the
			// user click twice (once to sync state, once to actually play).
			if(!this.isPlaying && radio.currentTrack) {
				radioLoadAndPlay(radio.currentTrack, false);
			}
			})();
		// Keep the player-area rating chips in sync with the current track's
		// state — on 'current' (a new track loaded) or 'rating' (chip
		// clicked, either from the player or from a library row).
		radio.subscribe(ev => {
			if(ev.type === 'current' || ev.type === 'rating') this._syncNowRating();
				if(ev.type === 'current' && this.isPlaying) this._startAdvanceTimer();
		});
		// Global spacebar → next random visualizer preset, gated on no
		// editable element being focused. Mirrors the macOS CodeRadio
		// app's space-key behavior. CodeMirror's editor area is
		// contenteditable, so typing space inside it still inserts a
		// space. randomPreset is a no-op when the viz isn't running, so
		// pressing space when the viz is off does nothing.
		window.addEventListener('keydown', e => {
			if(e.code !== 'Space' && e.key !== ' ') return;
			const ae = document.activeElement;
			const editable = ae && (
				ae.isContentEditable
				|| ae.tagName === 'INPUT' && !ae.disabled && !ae.readOnly
				|| ae.tagName === 'TEXTAREA' && !ae.disabled && !ae.readOnly
			);
			if(editable) return;
			e.preventDefault();
			visualizer.randomPreset();
		});
	}

	_syncNowRating() {
		const cur = radio.currentTrack;
		const up   = document.getElementById('control-rate-up');
		const down = document.getElementById('control-rate-down');
		const fav  = document.getElementById('control-rate-fav');
		if(!up || !down || !fav) return;
		const disabled = !cur;
		[up, down, fav].forEach(b => b.disabled = disabled);
		if(!cur) {
			[up, down, fav].forEach(b => b.classList.remove('is-active', 'is-down'));
			return;
		}
		const r = radio.getRating(cur.hash || cur.code);
		up.classList.toggle('is-active', r.rating === 'up');
		down.classList.toggle('is-active', r.rating === 'down');
		down.classList.toggle('is-down', r.rating === 'down');
		fav.classList.toggle('is-active', r.favorite);
	}

	/// Next / Prev clicks → ask radio for the next track in the active list
	/// (sequential or shuffle, lock-favorites respected; sequential wraps
	/// end-to-start since there's no Repeat button). Then load + play.
	/// dir is +1 (next) or -1 (previous).
	radioAdvance(dir) {
		this._clearAdvanceTimer();
		const track = dir > 0 ? radio.next() : radio.previous();
		if(!track) return;
		radioLoadAndPlay(track);
	}

	/// Bridge composer library clicks → radio.currentTrack. The user clicked
	/// a code-text or code-load button inside a `.entry`; walk up to the
	/// entry, read its data-hash, look up the track metadata in trackList's
	/// universe, and tell the radio so the player-area rating chips and
	/// sequential cursor know what's playing.
	_radioMarkFromEntry(elem) {
		const entry = elem.closest('.entry');
		if(!entry || !entry.dataset.hash) return;
		const track = trackList.universe.get(entry.dataset.hash);
		if(track) radio.setCurrent(track);
	}

	/// Mirror the toolbar mode buttons' `.is-active` class from radio.modes.
	_syncRadioToolbar() {
		const apply = (id, on) => {
			const el = document.getElementById(id);
			if(el) el.classList.toggle('is-active', !!on);
		};
		apply('control-shuffle',  radio.modes.shuffle);
		apply('control-lock-fav', radio.modes.lockFavorites);
		ui.initElements();
		scope.initElements();
		library.initElements();
		this.setVolume(true);
		this.setCounterUnits();
		this.setCodeStyle();
		this.setColorStereo();
		this.setColorDiagram();
		this.setColorWaveform();
		this.setColorTimeCursor();
		this.setScopeAdjustment(0);
		this.parseUrl();
		this.sendData({ drawMode: scope.drawMode });
		ui.controlDrawMode.value = scope.drawMode;
		ui.controlThemeStyle.value = this.settings.themeStyle;
		ui.controlCodeStyle.value = this.settings.codeStyle;
		ui.mainElem.addEventListener('click', this);
		ui.mainElem.addEventListener('change', this);
		ui.containerFixed.addEventListener('input', this);
		ui.containerFixed.addEventListener('keydown', this);
		ui.containerScroll.addEventListener('mouseover', this);
		this.setupMediaSession();
		this._setupHelpModal();
		this._setupSettingsModal();
	}
	// Hook hardware media keys + Control Center / Touch Bar Now Playing.
	// macOS routes media keys to whichever process owns the active audio
	// session — that's WebKit for us, so we have to handle them in JS via
	// the Web MediaSession API rather than relying on the native
	// MPRemoteCommandCenter wired on the Swift side.
	_setupHelpModal() {
		const modal = document.getElementById('coderadio-help-modal');
		if(!modal) return;
		modal.addEventListener('click', (e) => {
			// Dismiss only when the click hits the backdrop or the X
			// (both tagged with data-help-dismiss). Clicks on the card's
			// content shouldn't close.
			if(e.target.closest('[data-help-dismiss]')) {
				this._toggleHelpModal(false);
			}
		});
		document.addEventListener('keydown', (e) => {
			if(e.key === 'Escape' && !modal.classList.contains('is-hidden')) {
				this._toggleHelpModal(false);
			}
		});
	}
	_toggleHelpModal(show) {
		const modal = document.getElementById('coderadio-help-modal');
		if(!modal) return;
		modal.classList.toggle('is-hidden', !show);
	}
	_showPiano() {
		const container = document.getElementById('coderadio-piano');
		if (!container) return;
		if (this._pianoInstance) return; // already visible
		this._pianoOctave = this._pianoOctave || 0;
		const getBase = () => 48 + (this._pianoOctave || 0) * 12;
		container.classList.remove('is-hidden');
		// Button active state
		const btn = document.getElementById('control-piano');
		if (btn) btn.classList.add('is-active');
		this._pianoNotes = new Set();
		const addNote = (note) => {
			this._pianoNotes.add(note);
			midi.setVirtualNotes([...this._pianoNotes]);
			if (this.audioWorkletNode) this._syncMidiRatio();
		};
		const removeNote = (note) => {
			this._pianoNotes.delete(note);
			midi.setVirtualNotes([...this._pianoNotes]);
			if (this.audioWorkletNode) this._syncMidiRatio();
		};
		const buildPiano = () => {
			if (this._pianoInstance) this._pianoInstance.destroy();
			const base = getBase();
			this._pianoInstance = new Piano({
				container, midiRange: [base, base + 24],
				onKeyDown: (note) => addNote(note),
				onKeyUp: (note) => removeNote(note),
			});
		};
		buildPiano();
		// Full-keyboard musical typing — 3 octaves across all rows.
		// Semitone offsets from the piano's base MIDI note.
		const keyMap = {
			// Bottom row → lower white keys C–B
			'KeyZ':0,'KeyX':2,'KeyC':4,'KeyV':5,'KeyB':7,'KeyN':9,'KeyM':11,
			'Comma':12,'Period':14,'Slash':16,
			// Home row → middle octave (white keys C–B, black keys mixed)
			'KeyA':12,'KeyW':13,'KeyS':14,'KeyE':15,'KeyD':16,'KeyF':17,
			'KeyT':18,'KeyG':19,'KeyY':20,'KeyH':21,'KeyU':22,'KeyJ':23,'KeyK':24,
			'KeyL':26,'Semicolon':28,'Quote':29,
			// Top row → upper octave
			'KeyQ':24,'Digit2':25,'KeyW':26,'Digit3':27,'KeyE':28,
			'KeyR':29,'Digit5':30,'KeyT':31,'Digit6':32,'KeyY':33,
			'Digit7':34,'KeyU':35,'KeyI':36,'Digit9':37,'KeyO':38,'Digit0':39,'KeyP':40,
			'BracketLeft':41,'Equal':42,'BracketRight':43,
			// Number row extras
			'Digit1':23,'Digit4':29,'Digit8':36,'Minus':41,
		};
		const qwertyKeys = new Set(Object.keys(keyMap));
		this._qwertyDown = new Map();
		this._onPianoKeyDown = (e) => {
			if (!this._pianoInstance) return;
			if (qwertyKeys.has(e.code) && !e.repeat) {
				e.preventDefault();
				e.stopPropagation();
				addNote(getBase() + keyMap[e.code]);
				this._qwertyDown.set(e.code, getBase() + keyMap[e.code]);
				return;
			}
			// Block any letter/digit from typing into the editor while piano is open
			if (/^(Key|Digit|Comma|Period|Slash|Semicolon|Quote|Bracket|Minus|Equal)/.test(e.code)) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			if (e.code === 'ArrowLeft' || e.code === 'ArrowDown') {
				e.preventDefault(); e.stopPropagation();
				this._pianoOctave = Math.max(-3, (this._pianoOctave || 0) - 1);
				buildPiano();
			}
			if (e.code === 'ArrowRight' || e.code === 'ArrowUp') {
				e.preventDefault(); e.stopPropagation();
				this._pianoOctave = Math.min(3, (this._pianoOctave || 0) + 1);
				buildPiano();
			}
		};
		this._onPianoKeyUp = (e) => {
			if (!this._pianoInstance) return;
			if (this._qwertyDown.has(e.code)) {
				removeNote(this._qwertyDown.get(e.code));
				this._qwertyDown.delete(e.code);
			}
		};
		// Capture phase — intercept before CodeMirror sees the event
		document.addEventListener('keydown', this._onPianoKeyDown, true);
		document.addEventListener('keyup', this._onPianoKeyUp, true);
	}
	_hidePiano() {
		const container = document.getElementById('coderadio-piano');
		if (container) container.classList.add('is-hidden');
		const btn = document.getElementById('control-piano');
		if (btn) btn.classList.remove('is-active');
		if (this._pianoInstance) { this._pianoInstance.destroy(); this._pianoInstance = null; }
		if (this._onPianoKeyDown) { document.removeEventListener('keydown', this._onPianoKeyDown, true); this._onPianoKeyDown = null; }
		if (this._onPianoKeyUp)  { document.removeEventListener('keyup', this._onPianoKeyUp, true); this._onPianoKeyUp = null; }
		midi.setVirtualNotes([]);
		this._syncMidiRatio();
	}
		_setupSettingsModal() {
			if(this._settingsModalReady) return;
			this._settingsModalReady = true;
			const modal = document.getElementById('coderadio-settings-modal');
			if(!modal) return;
			// Dismiss on backdrop click or close-button click — any element
			// with data-settings-dismiss closes the modal.
			modal.querySelectorAll('[data-settings-dismiss]').forEach(el => {
				el.addEventListener('click', () => this._toggleSettingsModal(false));
			});
			document.addEventListener('keydown', (e) => {
				if(e.key === 'Escape' && !modal.classList.contains('is-hidden')) {
					this._toggleSettingsModal(false);
				}
			});
			// Wire checkboxes → live state + localStorage.
			const otn = document.getElementById('setting-one-touch-next');
			if(otn) {
				otn.addEventListener('change', () => {
					this.oneTouchNext = otn.checked;
					try { localStorage.setItem('coderadio.oneTouchNext', otn.checked ? '1' : '0'); } catch(_) {}
				});
			}
			const compact = document.getElementById('setting-compact');
			if(compact) {
				compact.addEventListener('change', () => {
					document.body.classList.toggle('compact-mode', compact.checked);
					try { localStorage.setItem('coderadio.compact', compact.checked ? '1' : '0'); } catch(_) {}
				});
			}
			// Continuous Play settings
			const cp = document.getElementById('setting-continuous-play');
			if(cp) {
				cp.addEventListener('change', () => {
					this.continuousPlay = cp.checked;
					try { localStorage.setItem('coderadio.continuousPlay', cp.checked ? '1' : '0'); } catch(_) {}
					const advOpts = document.getElementById('coderadio-advance-opts');
					if(advOpts) advOpts.classList.toggle('is-hidden', !cp.checked);
					// Restart timer when toggling on while playing
					if (cp.checked && this.isPlaying) this._startAdvanceTimer();
					else if (!cp.checked) this._clearAdvanceTimer();
				});
			}
			// Live code highlighting — per-mode toggles
			const hlTernary = document.getElementById('setting-hl-ternary');
			if(hlTernary) {
				hlTernary.addEventListener('change', () => {
					highlighter.ternary = hlTernary.checked;
					try { localStorage.setItem('coderadio.hlTernary', hlTernary.checked ? '1' : '0'); } catch(_) {}
				});
			}
			const hlSC = document.getElementById('setting-hl-shortcircuit');
			if(hlSC) {
				hlSC.addEventListener('change', () => {
					highlighter.shortCircuit = hlSC.checked;
					try { localStorage.setItem('coderadio.hlShortCircuit', hlSC.checked ? '1' : '0'); } catch(_) {}
				});
			}
			const hlShift = document.getElementById('setting-hl-bitshift');
			if(hlShift) {
				hlShift.addEventListener('change', () => {
					highlighter.bitShift = hlShift.checked;
					try { localStorage.setItem('coderadio.hlBitShift', hlShift.checked ? '1' : '0'); } catch(_) {}
				});
			}
			const hlGlow = document.getElementById('setting-hl-opglow');
			if(hlGlow) {
				hlGlow.addEventListener('change', () => {
					highlighter.operatorGlow = hlGlow.checked;
					try { localStorage.setItem('coderadio.hlOpGlow', hlGlow.checked ? '1' : '0'); } catch(_) {}
				});
			}
			// MIDI input
			const midiEl = document.getElementById('setting-midi-enabled');
			if(midiEl) {
				midiEl.addEventListener('change', async () => {
					midi.enabled = midiEl.checked;
					try { localStorage.setItem('coderadio.midiEnabled', midiEl.checked ? '1' : '0'); } catch(_) {}
					const midiOpts = document.getElementById('coderadio-midi-opts');
					if(midiOpts) midiOpts.classList.toggle('is-hidden', !midiEl.checked);
					if (midi.enabled) { await midi.start(); this._syncMidiRatio(); this._refreshMidiDevices(); }
					else { midi.stop(); this._syncMidiRatio(); this._refreshMidiDevices(); }
				});
			}
			const midiScale = document.getElementById('setting-midi-scale');
			if(midiScale) {
				midiScale.addEventListener('change', () => {
					midi.scale = midiScale.value;
					try { localStorage.setItem('coderadio.midiScale', midi.scale); } catch(_) {}
				});
			const midiMode = document.getElementById('setting-midi-mode');
			if(midiMode) {
				midiMode.addEventListener('change', () => {
					midi.transposeMode = midiMode.value;
					try { localStorage.setItem('coderadio.midiMode', midiMode.value); } catch(_) {}
					if (midi.enabled) this._syncMidiRatio();
				});
			}
			}
			const minEl = document.getElementById('setting-min-play-sec');
			if(minEl) {
				minEl.addEventListener('change', () => {
					this.minPlaySec = Math.max(5, parseInt(minEl.value, 10) || 30);
					try { localStorage.setItem('coderadio.minPlaySec', String(this.minPlaySec)); } catch(_) {}
					if (this.continuousPlay && this.isPlaying) this._startAdvanceTimer();
				});
			}
			const maxEl = document.getElementById('setting-max-loop-sec');
			if(maxEl) {
				maxEl.addEventListener('change', () => {
					this.maxLoopSec = Math.max(10, parseInt(maxEl.value, 10) || 120);
					try { localStorage.setItem('coderadio.maxLoopSec', String(this.maxLoopSec)); } catch(_) {}
					if (this.continuousPlay && this.isPlaying) this._startAdvanceTimer();
				});
			}
			const evoEl = document.getElementById('setting-evolving-max-sec');
			if(evoEl) {
				evoEl.addEventListener('change', () => {
					this.evolvingMaxSec = Math.max(30, parseInt(evoEl.value, 10) || 3600);
					try { localStorage.setItem('coderadio.evolvingMaxSec', String(this.evolvingMaxSec)); } catch(_) {}
					if (this.continuousPlay && this.isPlaying) this._startAdvanceTimer();
				});
			}
		}
		_syncSettingsModal() {
			const otn = document.getElementById('setting-one-touch-next');
			if(otn) otn.checked = this.oneTouchNext;
			const compact = document.getElementById('setting-compact');
			if(compact) compact.checked = document.body.classList.contains('compact-mode');
			// Live code highlighting
			const hlTernary = document.getElementById('setting-hl-ternary');
			if(hlTernary) hlTernary.checked = highlighter.ternary;
			const hlSC = document.getElementById('setting-hl-shortcircuit');
			if(hlSC) hlSC.checked = highlighter.shortCircuit;
			const hlShift = document.getElementById('setting-hl-bitshift');
			if(hlShift) hlShift.checked = highlighter.bitShift;
			const hlGlow = document.getElementById('setting-hl-opglow');
			if(hlGlow) hlGlow.checked = highlighter.operatorGlow;
			// Continuous Play
			const cp = document.getElementById('setting-continuous-play');
			if(cp) cp.checked = this.continuousPlay;
			const advOpts = document.getElementById('coderadio-advance-opts');
			if(advOpts) advOpts.classList.toggle('is-hidden', !this.continuousPlay);
			const minEl = document.getElementById('setting-min-play-sec');
			if(minEl) minEl.value = this.minPlaySec;
			const maxEl = document.getElementById('setting-max-loop-sec');
			if(maxEl) maxEl.value = this.maxLoopSec;
			const evoEl = document.getElementById('setting-evolving-max-sec');
			if(evoEl) evoEl.value = this.evolvingMaxSec;
		// MIDI
		const midiEl = document.getElementById('setting-midi-enabled');
		if(midiEl) midiEl.checked = midi.enabled;
		const midiScale = document.getElementById('setting-midi-scale');
		if(midiScale) midiScale.value = midi.scale;
		const midiMode = document.getElementById('setting-midi-mode');
		if(midiMode) midiMode.value = midi.transposeMode;
		const midiOpts = document.getElementById('coderadio-midi-opts');
		if(midiOpts) midiOpts.classList.toggle('is-hidden', !midi.enabled);
		}
		_toggleSettingsModal(show) {
			const modal = document.getElementById('coderadio-settings-modal');
			if(!modal) return;
			if(show) { this._syncSettingsModal(); this._refreshMidiDevices(); }
			modal.classList.toggle('is-hidden', !show);
		}
	_refreshMidiDevices() {
		const el = document.getElementById('coderadio-midi-devices');
		if (!el) return;
		if (!midi._access) { el.textContent = 'Approve the browser prompt, then reopen settings.'; return; }
		const inputs = [...midi._access.inputs.values()];
		if (inputs.length === 0) {
			el.innerHTML = 'No MIDI devices found. macOS: Audio MIDI Setup → Window → Show MIDI Studio → double-click IAC Driver → Device is online.';
			return;
		}
		el.innerHTML = inputs.map(i => `🎹 ${i.name || i.id}`).join(' · ');
	}
		/// Continuous Play — compute play duration from loopSamples + settings,
		/// then set a timer that auto-advances to the next track.
		_computePlayDuration(track) {
			if (!track) return this.evolvingMaxSec * 1000;
			const sr = track.sampleRate || 8000;
			const key = track.hash || track.code;
			const loopSamples = LOOP_SAMPLES[key];
			if (loopSamples != null && loopSamples > 0) {
				const loopSec = loopSamples / sr;
				if (loopSec >= this.maxLoopSec) {
					// Single loop exceeds max — play once, advance after full loop
					return loopSec * 1000;
				}
				// Loop until minimum play time is met
				const loops = Math.ceil(this.minPlaySec / loopSec);
				return loops * loopSec * 1000;
			}
			// No loop detected — evolving track, cap at limit
			return this.evolvingMaxSec * 1000;
		}
		_startAdvanceTimer() {
			this._clearAdvanceTimer();
			if (!this.continuousPlay) return;
			const ms = this._computePlayDuration(radio.currentTrack);
			this._advanceTimer = setTimeout(() => {
				this._advanceTimer = null;
				this.radioAdvance(1);
			}, ms);
		}
		_clearAdvanceTimer() {
			if (this._advanceTimer != null) {
				clearTimeout(this._advanceTimer);
				this._advanceTimer = null;
			}
		}
	setupMediaSession() {
		if(!('mediaSession' in navigator)) {
			return;
		}
		// Guard against duplicate setup: _syncRadioToolbar() calls this
		// on every shuffle/lock toggle, and the boot path calls it twice.
		// Without this, we'd create orphan backing-audio elements and
		// duplicate 'current' subscribers every time the user toggled a mode.
		if(this._mediaSessionReady) return;
		this._mediaSessionReady = true;
		const ms = navigator.mediaSession;
		// One Touch Next: when enabled, a single headset/notification press
		// skips to the next track while playing, instead of pausing. This
		// fixes the common Bluetooth problem where a double-tap to skip
		// registers as a single press and accidentally pauses. Pausing is
		// still available via the on-screen button (which calls
		// playbackToggle directly, not via mediaSession).
		try { this.oneTouchNext = localStorage.getItem('coderadio.oneTouchNext') === '1'; } catch(_) { this.oneTouchNext = false; }
		const singlePress = (wouldBePlaying) => {
			if(this.oneTouchNext && this.isPlaying) {
				this.radioAdvance(1);
			} else {
				this.playbackToggle(wouldBePlaying, true);
			}
		};
		// Play / Pause: composer's built-in audio toggle. playbackToggle()
		// is the single owner of mediaSession.playbackState (and of the
		// backing element's play/pause + the AudioContext resume), so the
		// handlers just call it — no separate playbackState writes here, to
		// avoid racing two sources of truth for the widget's icon.
		ms.setActionHandler('play', () => this.playbackToggle(true, true));
		ms.setActionHandler('pause', () => singlePress(false));
		// Some OSes route to a single toggle action instead of separate
		// play/pause. Register the toggle as a safety net so a single
		// keypress always flips state correctly.
		try {
			ms.setActionHandler('togglePlayPause', () => singlePress(!this.isPlaying));
		} catch(e) {}
		// Next / Previous: walk the radio's active library list
		// (sequential or weighted-shuffle depending on mode).
		// With One Touch Next, single-press already skips — repurpose
		// the double-press (nexttrack) as play/pause so the user still
		// has full control from the headset.
		try {
			ms.setActionHandler('nexttrack', () => {
				if(this.oneTouchNext) {
					this.playbackToggle(!this.isPlaying, true);
				} else {
					this.radioAdvance(1);
				}
			});
			ms.setActionHandler('previoustrack', () => this.radioAdvance(-1));
		} catch(e) { /* unsupported in older browsers */ }
		// Backing <audio> element for OS media-session integration.
		// Android Chrome and iOS Safari require a real HTMLMediaElement
		// behind the session — a bare WebAudio AudioContext isn't enough
		// to get the lock-screen widget, notification card, or hardware
		// media keys. That's a platform constraint, not something unique
		// to this app. The element carries makeSilenceWavUrl()'s real,
		// decodable, >5 s looping silence so Chrome grants it "full audio
		// focus" (persistent notification + AVRCP routing).
		//
		// Lifecycle is deliberately the STANDARD media-app pattern:
		// - Created here but NOT started. It is never muted and never
		//   autoplayed on load. (Muted media gets no notification, and a
		//   muted-then-unmuted element doesn't reliably re-activate the
		//   session — that combination is what broke the card before.)
		// - .play()'d on the first real play GESTURE (in-app button,
		//   media key, lock-screen widget) in playbackToggle(). That's
		//   when the notification card first appears — exactly like
		//   Spotify/YouTube, which also show nothing until you hit play.
		// - .pause()'d in lockstep with bytebeat on every pause, on ALL
		//   platforms. Pausing a real-duration element keeps the session
		//   alive in a "paused" state (the card stays, showing a play
		//   button); resuming fires a genuine paused→playing transition,
		//   which is the user-activation the suspended AudioContext needs
		//   to wake from a backgrounded screen. (The old zero-duration
		//   WAV couldn't survive a pause — the session was torn down,
		//   which is why pausing from Bluetooth made the card vanish.)
		const sessionAudio = document.createElement('audio');
		sessionAudio.src = makeSilenceWavUrl();
		sessionAudio.loop = true;
		sessionAudio.preload = 'auto';
		sessionAudio.setAttribute('playsinline', '');
		sessionAudio.setAttribute('aria-hidden', 'true');
		sessionAudio.style.display = 'none';
		document.body.appendChild(sessionAudio);
		this._sessionAudio = sessionAudio;
		// Keep the OS widget's metadata in sync with whatever the radio
		// just loaded. Fires on Next/Prev, user track-clicks, and
		// last-track restore on page open.
		//
		// playbackState must reflect this.isPlaying, NOT be hardcoded to
		// 'playing' — a track can become "current" on page load (default
		// 42-melody, restored last track) before the user has pressed
		// play, since we deliberately don't auto-play (browser autoplay
		// policy). Claiming 'playing' while no audio is actually flowing
		// makes some mobile browsers treat the media session as a dead
		// session — the OS widget stays visible but stops forwarding
		// button presses to the page entirely.
		radio.subscribe(ev => {
			if(ev.type !== 'current' || !ev.track) return;
			this._updateMediaMetadata();
			ms.playbackState = this.isPlaying ? 'playing' : 'paused';
		});
	}
	/// Set the OS Now Playing card's metadata from the radio's current
	/// track. Kept separate from playbackState (which playbackToggle owns):
	/// setting metadata on an inactive session is harmless, but claiming
	/// playbackState='playing' with no audio flowing makes some mobile
	/// browsers treat the session as dead. Called on every 'current' event,
	/// once at boot after the restored/default track is established, and on
	/// the play gesture so the card is right the instant it first appears.
	_updateMediaMetadata() {
		if(!('mediaSession' in navigator)) return;
		const t = radio.currentTrack;
		if(!t) return;
		const title = (t.description && String(t.description).trim()) ||
			(t.code ? String(t.code).slice(0, 60) : 'bytebeat');
		try {
			navigator.mediaSession.metadata = new MediaMetadata({
				title,
				artist: t.author || 'unknown',
				album:  'bytebeat-composer',
			});
		} catch(_) {}
	}
	// Bridge to CodeRadio's RadioStation. No-op when running in a browser
	// without the WKScriptMessageHandler registered (e.g. dev preview).
	postCoderadio(action) {
		if(globalThis.webkit && globalThis.webkit.messageHandlers
			&& globalThis.webkit.messageHandlers.coderadio) {
			globalThis.webkit.messageHandlers.coderadio.postMessage(action);
		}
	}
	async initAudio() {
		this.audioCtx = new AudioContext({ latencyHint: 'balanced', sampleRate: 48000 });
		this.audioGain = new GainNode(this.audioCtx);
		this.audioGain.connect(this.audioCtx.destination);
		// Analyser for FFT mode
		scope.analyser = [this.audioCtx.createAnalyser(), this.audioCtx.createAnalyser()];
		scope.analyser[0].minDecibels = scope.analyser[1].minDecibels = scope.minDecibels;
		scope.analyser[0].maxDecibels = scope.analyser[1].maxDecibels = scope.maxDecibels;
		scope.setFFTAnalyzer();
		const splitter = this.audioCtx.createChannelSplitter(2);
		splitter.connect(scope.analyser[0], 0);
		splitter.connect(scope.analyser[1], 1);
		const analyserGain = new GainNode(this.audioCtx);
		analyserGain.connect(splitter);
		// AudioWorklet for main calculations processing
		await this.audioCtx.audioWorklet.addModule('./build/audio-processor.mjs');
		this.audioWorkletNode = new AudioWorkletNode(this.audioCtx, 'audioProcessor',
			{ outputChannelCount: [2] });
		this.audioWorkletNode.port.addEventListener('message', event => this.receiveData(event.data));
		this.audioWorkletNode.port.start();
		this.audioWorkletNode.connect(this.audioGain);
		this.audioWorkletNode.connect(analyserGain);
		// Hook Butterchurn to the audio output, now that the AudioContext +
		// worklet exist. If the user has the viz toggled on (preference
		// persisted), this will kick off the render loop immediately.
		visualizer.attachAudio(this.audioCtx, this.audioWorkletNode);
		vibingCat.attachAudio(this.audioCtx, this.audioWorkletNode);
		// Recorder for recording audio files
		const mediaDest = this.audioCtx.createMediaStreamDestination();
		const audioRecorder = this.audioRecorder = new MediaRecorder(mediaDest.stream);
		audioRecorder.addEventListener('dataavailable', event => this.audioRecordChunks.push(event.data));
		audioRecorder.addEventListener('stop', () => {
			let fileName, type;
			const types = ['audio/webm', 'audio/ogg'];
			const files = ['track.webm', 'track.ogg'];
			while((fileName = files.pop()) && !MediaRecorder.isTypeSupported(type = types.pop())) {
				if(types.length === 0) {
					console.error('Recording is not supported in this browser!');
					break;
				}
			}
			const url = URL.createObjectURL(new Blob(this.audioRecordChunks, { type }));
			ui.downloader.href = url;
			ui.downloader.download = fileName;
			ui.downloader.click();
			setTimeout(() => window.URL.revokeObjectURL(url));
		});
		this.audioGain.connect(mediaDest);
	}
	loadCode({ code, sampleRate, mode, drawMode, scale }, isPlay = true) {
		this.mode = ui.controlPlaybackMode.value = mode = mode || 'Bytebeat';
		editor.setValue(code);
		highlighter.updateCode(code);
		this.setSampleRate(ui.controlSampleRate.value = +sampleRate || 8000, false);
		midi.setReferenceSampleRate(+sampleRate || 8000);
		this.setSRDivisor(0);
		const data = {
			mode,
			sampleRate: this.sampleRate,
			sampleRatio: this.sampleRate / this.audioCtx.sampleRate
		};
		if(isPlay) {
			data.playbackSpeed = this.playbackSpeed = 1;
			this.playbackToggle(true, false);
			data.resetTime = true;
			data.isPlaying = isPlay;
		}
		data.setFunction = code;
		if(drawMode) {
			ui.controlDrawMode.value = scope.drawMode = drawMode;
			scope.toggleTimeCursor();
			scope.clearCanvas();
			this.saveSettings();
		}
		if(scale !== undefined) {
			this.setScale(scale - scope.drawScale);
		}
		this.sendData(data);
	}
	oninputCounter(event) {
		if(event.key === 'Enter') {
			ui.controlTime.blur();
			this.playbackToggle(true);
			return;
		}
		const byteSample = this.settings.isSeconds ? Math.round(ui.controlTime.value * this.sampleRate) :
			ui.controlTime.value;
		this.setByteSample(byteSample);
		this.sendData({ byteSample });
	}
	parseUrl() {
		let urlHash = window.location.hash;
		if(!urlHash) {
			this.updateUrl();
			urlHash = window.location.hash;
		}
		this.loadCode(getCodeFromUrl(urlHash) || { code: editor.value }, false);
	}
	playbackStop() {
		this.playbackToggle(false, false);
		this.sendData({ isPlaying: false, resetTime: true });
	}
	playbackToggle(isPlaying, isSendData = true, speedIncrement = 0) {
		const isReverse = speedIncrement ? speedIncrement < 0 : this.playbackSpeed < 0;
		const buttonElem = isReverse ? ui.controlPlayBackward : ui.controlPlayForward;
		if(speedIncrement && buttonElem.getAttribute('disabled')) {
			return;
		}
		const multiplierElem = buttonElem.firstElementChild;
		const speed = speedIncrement ? +multiplierElem.textContent : 1;
		multiplierElem.classList.toggle('control-fast-multiplier-large', speed >= 8);
		const nextSpeed = speed === 64 ? 0 : speed * 2;
		ui.setPlayButton(ui.controlPlayBackward, isPlaying && isReverse ? nextSpeed : 1);
		ui.setPlayButton(ui.controlPlayForward, isPlaying && !isReverse ? nextSpeed : 1);
		if(speedIncrement || !isPlaying) {
			this.playbackSpeed = isPlaying ? speedIncrement * speed : Math.sign(this.playbackSpeed);
		}
		scope.canvasContainer.title = isPlaying ? `Click to ${
			this.isRecording ? 'pause and stop recording' : 'pause' }` :
			`Click to play${ isReverse ? ' in reverse' : '' }`;
		scope.canvasPlayButton.classList.toggle('canvas-play-backward', isReverse);
		scope.canvasPlayButton.classList.toggle('canvas-play', !isPlaying);
		scope.canvasPlayButton.classList.toggle('canvas-pause', isPlaying);
		if(isPlaying) {
			scope.canvasPlayButton.classList.remove('canvas-initial');
			// Resume the AudioContext and start the session-backing
			// <audio> SYNCHRONOUSLY, both inside this handler's call
			// stack. This runs straight off a user gesture (in-app
			// click, media key, or lock-screen 'play' action), and the
			// gesture's activation is only valid for synchronous work —
			// the moment we cross an `await`/`.then()` microtask
			// boundary it's gone. The previous code chained resume()
			// inside `play().then(...)`, so on mobile (where the OS
			// suspends the AudioContext on screen-lock) resume() ran
			// without activation and silently failed — the notification
			// icon flashed to pause then snapped back with no audio.
			// Order doesn't matter as long as both are synchronous;
			// resume() first guarantees the context wakes even if the
			// element is momentarily busy.
			if(this.audioCtx.resume) {
				this.audioCtx.resume().catch(e => console.warn('[audio] resume failed:', e));
			}
			if(this._sessionAudio) {
				this._sessionAudio.play().catch(() => {});
			}
			// The backing element only starts here, on the play gesture, so
			// this is the first moment the OS card appears. Refresh metadata
			// now (synchronous, no gesture consumed) so the card shows THIS
			// track immediately, even if the boot-time set didn't stick to a
			// then-inactive session.
			this._updateMediaMetadata();
			scope.requestAnimationFrame(); // Main call for drawing in the scope
			this._startAdvanceTimer();
			if (midi.enabled) midi.start();
		} else {
			this._clearAdvanceTimer();
			editor.clearActiveRanges();
			if (codeCanvas) codeCanvas.tick([], 0);
			this.lastUpdateTime = 0;
			this.updateCounter = 0;
			this.isLagging = false;
			ui.controlLag.innerText = '---';
			ui.controlLag.classList.remove('control-lag-red');
			if(this.isRecording) {
				this.isRecording = false;
				ui.controlRecord.classList.remove('control-recording');
				ui.controlRecord.title = 'Record to file';
				this.audioRecorder.stop();
			}
		}
		this.isPlaying = isPlaying;
		if(isSendData) {
			this.sendData({ isPlaying, playbackSpeed: this.playbackSpeed });
		} else {
			this.isNeedClear = true;
		}
		// Keep the OS Now Playing widget in sync regardless of how
		// play/pause was triggered (toolbar button, canvas click, media
		// keys, etc). Without this, macOS Now Playing routes F8 to the
		// stale state — e.g. you pause via the on-screen button, then
		// F8 fires 'pause' again instead of 'play'.
		if('mediaSession' in navigator) {
			navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
		}
		// Pause the session-backing <audio> in lockstep with bytebeat, on
		// ALL platforms. This is the ordinary media-app contract and it's
		// what makes resume work everywhere:
		//
		// - The element is a real, >5 s, decodable clip, so pausing it
		//   leaves the media session ALIVE in a "paused" state — the Now
		//   Playing card / lock-screen widget stays put, showing a play
		//   button. (The old zero-duration WAV had nothing to sustain, so
		//   a pause tore the session down and the card vanished — the
		//   original "pause from Bluetooth makes the card disappear"
		//   complaint. That's fixed at the source now, so the previous
		//   mobile-only "never pause" hack, and its desktop media-key
		//   trade-off, are both gone.)
		// - Keeping element state and playbackState consistent (both
		//   "paused") stops Chrome auto-correcting the notification icon.
		// - On resume, .play() (in the isPlaying branch above) fires a
		//   real paused->playing transition — the user-activation that a
		//   screen-lock-suspended AudioContext needs in order to wake.
		if(!isPlaying && this._sessionAudio) {
			this._sessionAudio.pause();
		}
	}
	receiveData(data) {
		const { byteSample, drawBuffer, error } = data;
		if(typeof byteSample === 'number') {
			this.setCounterValue(byteSample);
			this.setByteSample(byteSample);
			// Live code highlighting — canvas overlay + editor outlines
			const ranges = highlighter.tick(byteSample);
			if (ranges) {
				if (codeCanvas) codeCanvas.tick(ranges, byteSample);
				editor.setActiveRanges(ranges);
			}
		}
		if(data.pcm) {
			this.postCoderadio({ pcm: Array.from(data.pcm) });
		}
		if(Array.isArray(drawBuffer)) {
			scope.drawBuffer = scope.drawBuffer.concat(drawBuffer);
			const limit = scope.canvasWidth * (1 << scope.drawScale) - 1;
			if(scope.drawBuffer.length > limit) {
				scope.drawBuffer = scope.drawBuffer.slice(-limit);
			}
		}
		if(error !== undefined) {
			let isUpdate = false;
			if(error.isCompiled === false) {
				isUpdate = true;
				this.isCompilationError = true;
			} else if(error.isCompiled === true) {
				isUpdate = true;
				this.isCompilationError = false;
			} else if(error.isRuntime === true && !this.isCompilationError) {
				isUpdate = true;
			}
			if(isUpdate) {
				editor.errorElem.innerText = error.message;
				this.sendData({ errorDisplayed: true });
			}
			if(data.updateUrl !== true) {
				ui.setCodeSize(editor.value);
			}
		}
		if(data.updateUrl === true) {
			this.updateUrl();
		}
	}
	resetScopeAdjustment() {
		if(scope.drawMode === 'FFT') {
			this.setFFTBins(-scope.fftSize + 10);
		} else {
			this.setScale(-scope.drawScale);
		}
	}
	resetTime() {
		this.isNeedClear = true;
		this.sendData({ resetTime: true, playbackSpeed: this.playbackSpeed });
	}
	saveSettings() {
		this.settings.drawMode = scope.drawMode;
		this.settings.drawScale = scope.drawScale;
		this.settings.fftSize = scope.fftSize;
		this.settings.showAllSongs = library.showAllSongs;
		localStorage.settings = JSON.stringify(this.settings);
	}
	sendData(data) {
		this.audioWorkletNode.port.postMessage(data);
	}
	setByteSample(value) {
		this.byteSample = +value || 0;
		if(this.isNeedClear && value === 0) {
			this.isNeedClear = false;
			scope.drawBuffer = [];
			scope.canvasTimeCursor.style.left = 0;
			scope.clearCanvas();
			if(!this.isPlaying) {
				scope.canvasPlayButton.classList.add('canvas-initial');
			}
		}
	}
	setCodeStyle(value) {
		if(value !== undefined) {
			this.settings.codeStyle = value;
			this.saveSettings();
		} else if((value = this.settings.codeStyle) === undefined) {
			value = this.settings.codeStyle = this.defaultSettings.codeStyle;
			this.saveSettings();
		}
		document.documentElement.dataset.syntax = value;
		document.documentElement.dataset.syntaxType = value.endsWith('Light') ? 'light' : 'dark';
	}
	setColorDiagram(value) {
		if(value !== undefined) {
			this.settings.colorDiagram = value;
			this.saveSettings();
		} else if((value = this.settings.colorDiagram) === undefined) {
			value = this.settings.colorDiagram = this.defaultSettings.colorDiagram;
			this.saveSettings();
		}
		ui.controlColorDiagram.value = value;
		ui.controlColorDiagramInfo.innerHTML = scope.getColorTest('colorDiagram', value);
	}
	setColorStereo(value) {
		// value: Red=0, Green=1, Blue=2
		if(value !== undefined) {
			this.settings.colorStereo = value;
			this.saveSettings();
		} else if((value = this.settings.colorStereo) === undefined) {
			value = this.settings.colorStereo = this.defaultSettings.colorStereo;
			this.saveSettings();
		}
		ui.controlColorStereo.value = value;
		switch(value) {
		// [Left, Right1, Right2]
		case 0: scope.colorChannels = [0, 1, 2]; break;
		case 2: scope.colorChannels = [2, 0, 1]; break;
		default: scope.colorChannels = [1, 0, 2];
		}
		if(scope.colorWaveform) {
			scope.setStereoColors();
		}
	}
	setColorTimeCursor(value) {
		if(value !== undefined) {
			this.settings.colorTimeCursor = value;
			this.saveSettings();
		} else if((value = this.settings.colorTimeCursor) === undefined) {
			value = this.settings.colorTimeCursor = this.defaultSettings.colorTimeCursor;
			this.saveSettings();
		}
		ui.controlColorTimeCursor.value = value;
		scope.canvasTimeCursor.style.borderLeft = '2px solid ' + value;
	}
	setColorWaveform(value) {
		if(value !== undefined) {
			this.settings.colorWaveform = value;
			this.saveSettings();
		} else if((value = this.settings.colorWaveform) === undefined) {
			value = this.settings.colorWaveform = this.defaultSettings.colorWaveform;
			this.saveSettings();
		}
		ui.controlColorWaveform.value = value;
		ui.controlColorWaveformInfo.innerHTML = scope.getColorTest('colorWaveform', value);
		scope.setStereoColors();
	}
	setCounterUnits() {
		ui.controlTimeUnits.textContent = this.settings.isSeconds ? 'sec' : 't';
		this.setCounterValue(this.byteSample);
	}
	setCounterValue(value) {
		ui.controlTime.value = this.settings.isSeconds ? (value / this.sampleRate).toFixed(2) : value;
		// Lag detection
		this.updateCounter++;
		if(this.updateCounter === 400) {
			this.updateCounter = 0;
			const time = Date.now();
			if(this.lastUpdateTime) {
				const lag =
					Math.min(Math.max(Math.round((time - this.lastUpdateTime) * 37.5 / 400) - 100, 0), 999);
				ui.controlLag.innerText = lag + '%';
				if(lag > 3) {
					if(!this.isLagging) {
						this.isLagging = true;
						ui.controlLag.classList.add('control-lag-red');
					}
				} else if(this.isLagging) {
					this.isLagging = false;
					ui.controlLag.classList.remove('control-lag-red');
				}
			}
			this.lastUpdateTime = time;
		}
	}
	setDrawMode(drawMode) {
		scope.drawMode = drawMode;
		this.setScopeAdjustment(0);
		scope.toggleTimeCursor();
		scope.clearCanvas();
		this.saveSettings();
		this.sendData({ drawMode });
	}
	setFFTBins(amount, buttonElem) {
		if(buttonElem?.getAttribute('disabled')) {
			return;
		}
		scope.setFFTSize(scope.fftSize + amount);
		scope.setFFTAnalyzer();
		scope.clearCanvas();
		this.saveSettings();
		ui.setControlScale(scope.fftSize >= 15, scope.fftSize <= 5,
			scope.fftSize < 10 ? 2 ** scope.fftSize : `<sub>2</sub>${ scope.fftSize }`);
	}
	setPlaybackMode(mode) {
		this.mode = mode;
		this.updateUrl();
		this.sendData({ mode, setFunction: editor.value });
	}
	setSampleRate(sampleRate, isSendData = true) {
		if(!sampleRate || !isFinite(sampleRate) ||
			// Float32 limit
			(sampleRate = Number(parseFloat(Math.abs(sampleRate)).toFixed(3))) > 3.4028234663852886E+38
		) {
			sampleRate = 8000;
		}
		sampleRate = Math.max(0.1, sampleRate);
		switch(sampleRate) {
		case 8000:
		case 11025:
		case 16000:
		case 22050:
		case 32000:
		case 44100:
		case 48000: ui.controlSampleRateSelect.value = sampleRate; break;
		default: ui.controlSampleRateSelect.selectedIndex = -1;
		}
		const oldSampleRate = this.sampleRate;
		ui.controlSampleRate.value = this.sampleRate = sampleRate;
		ui.controlSampleRate.blur();
		ui.controlSampleRateSelect.blur();
		scope.toggleTimeCursor();
		if(isSendData) {
			const data = {
				sampleRate: this.sampleRate,
				sampleRatio: this.sampleRate / this.audioCtx.sampleRate
			};
			if(this.mode === 'Funcbeat') {
				data.byteSample = Math.round(ui.controlTime.value * sampleRate /
					(this.settings.isSeconds ? 1 : oldSampleRate));
				this.setCounterValue(data.byteSample);
				this.setByteSample(data.byteSample);
			}
			this.updateUrl();
			this.sendData(data);
		}
	}
		// MIDI transposition — apply current ratio to the audio worklet
		// without changing the UI-displayed sample rate.
	_syncMidiRatio() {
		if (!this.audioWorkletNode) return;
		const ratio = (midi.enabled || midi._virtualNotes.size > 0) ? midi.currentRatio : 1;
		if (midi.transposeMode === 'pitch') {
			this.sendData({
				sampleRatio: this.sampleRate / this.audioCtx.sampleRate,
				pitchRatio: ratio,
			});
		} else {
			this.sendData({
				sampleRatio: (this.sampleRate * ratio) / this.audioCtx.sampleRate,
				pitchRatio: 1,
			});
		}
	}
	setScale(amount, buttonElem) {
		if(buttonElem?.getAttribute('disabled')) {
			return;
		}
		scope.drawScale = Math.min(Math.max(scope.drawScale + amount, 0), 20);
		scope.toggleTimeCursor();
		scope.clearCanvas();
		this.saveSettings();
		ui.setControlScale(scope.drawScale <= 0, scope.drawScale >= 20,
			!scope.drawScale ? '1x' :
			scope.drawScale < 7 ? `1/${ 2 ** scope.drawScale }${ scope.drawScale < 4 ? 'x' : '' }` :
			`<sub>2</sub>-${ scope.drawScale }`);
	}
	setScopeAdjustment(amount, buttonElem) {
		if(scope.drawMode === 'FFT') {
			ui.controlScaleDown.title = 'Use more FFT bins';
			ui.controlScaleUp.title = 'Use less FFT bins';
			ui.controlScale.title = 'FFT bins. Click to reset to 1024';
			this.setFFTBins(-amount, buttonElem);
		} else {
			ui.controlScaleDown.title = 'Zoom in the scope';
			ui.controlScaleUp.title = 'Zoom out the scope';
			ui.controlScale.title = 'Scope zoom factor. Click to reset to 1.';
			this.setScale(amount, buttonElem);
		}
	}
	setSRDivisor(increment) {
		const value = (this.settings.srDivisor || 1) + increment;
		if(value === 0) {
			return;
		}
		ui.controlSRDivisor.textContent = this.settings.srDivisor = value;
		this.saveSettings();
		this.sendData({ srDivisor: value });
	}
	setThemeStyle(value) {
		if(value === undefined) {
			if((value = this.settings.themeStyle) === undefined) {
				value = this.settings.themeStyle = this.defaultSettings.themeStyle;
				this.saveSettings();
			}
			document.documentElement.dataset.theme = value;
			document.documentElement.dataset.themeType = value.endsWith('Light') ? 'light' : 'dark';
			return;
		}
		document.documentElement.dataset.theme = this.settings.themeStyle = value;
		document.documentElement.dataset.themeType = value.endsWith('Light') ? 'light' : 'dark';
		let colorCursor, colorDiagram;
		let colorStereo = 1; // Red=0, Green=1, Blue=2
		switch(value) {
		case 'Cake Dark':
			colorCursor = '#40ffff';
			colorDiagram = '#c000c0';
			colorStereo = 0;
			break;
		case 'Green Dark':
			colorCursor = '#00ffa8';
			colorDiagram = '#00a080';
			break;
		case 'Orange Dark':
			colorCursor = '#ffff80';
			colorDiagram = '#8000ff';
			colorStereo = 0;
			break;
		case 'Purple Dark':
			colorCursor = '#ff50ff';
			colorDiagram = '#a040ff';
			colorStereo = 0;
			break;
		case 'Teal Dark':
			colorCursor = '#80c0ff';
			colorDiagram = '#00a0c0';
			break;
		default: // Blue Dark, Dusk Dark, Default Dark, Default Light
			colorCursor = '#80c0ff';
			colorDiagram = '#0080ff';
		}
		this.setColorTimeCursor(colorCursor);
		this.setColorStereo(colorStereo);
		ui.controlColorWaveformInfo.innerHTML = scope.getColorTest('colorWaveform');
		this.setColorDiagram(ui.controlColorDiagram.value = colorDiagram); // Contains this.saveSettings();
	}
	setVolume(isInit) {
		let volumeValue = NaN;
		if(isInit) {
			volumeValue = parseFloat(this.settings.volume);
		}
		if(isNaN(volumeValue)) {
			volumeValue = ui.controlVolume.value / ui.controlVolume.max;
		}
		ui.controlVolume.value = this.settings.volume = volumeValue;
		ui.controlVolume.title = `Volume: ${ (volumeValue * 100).toFixed(2) }%`;
		this.saveSettings();
		this.audioGain.gain.value = volumeValue * volumeValue;
	}
	toggleCounterUnits() {
		this.settings.isSeconds = !this.settings.isSeconds;
		this.saveSettings();
		this.setCounterUnits();
	}
	toggleRecording() {
		if(!this.audioCtx) {
			return;
		}
		if(this.isRecording) {
			this.playbackToggle(false);
			return;
		}
		this.isRecording = true;
		ui.controlRecord.classList.add('control-recording');
		ui.controlRecord.title = 'Pause and stop recording';
		this.audioRecorder.start();
		this.audioRecordChunks = [];
		this.playbackToggle(true);
	}
	updateUrl() {
		const code = editor.value;
		ui.setCodeSize(code);
		getUrlFromCode(code, this.mode, this.sampleRate);
	}
}();
